import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { ArrowLeft, Loader2, Printer } from "lucide-react";

import { GET_SITUATION_FOR_GROWNUP } from "@/graphql/queries/situations";
import {
  PrintedSheet,
  type PrintedQuestion,
} from "@/components/situations/printed-sheet";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

/**
 * One situation on paper.
 *
 * The sheet itself is `PrintedSheet`, shared with the whole-session print view —
 * the same sheet printed alone and inside a set must not drift apart. This page is
 * the three choices a grown-up makes before pressing print.
 */
export default function PrintSituationPage() {
  const { key = "" } = useParams();
  const [params] = useSearchParams();
  const studentId = params.get("student");
  const sessionId = params.get("session");

  const [showNotes, setShowNotes] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [onePerPage, setOnePerPage] = useState(false);

  const { data, loading } = useQuery(GET_SITUATION_FOR_GROWNUP, {
    variables: { studentId, key, sessionId },
    skip: !studentId || !key,
  });

  const situation = (data as any)?.situation;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!situation) {
    return (
      <div className="py-12 text-center text-muted-foreground">Situacija nerasta</div>
    );
  }

  const questions: PrintedQuestion[] = situation.questions ?? [];
  const hasAnswers = questions.some(
    (q) => q.answer || q.reasonAnswer || q.counterAnswer
  );
  const backTo = sessionId
    ? `/lab/${key}?session=${sessionId}&student=${studentId}`
    : `/lab/${key}?student=${studentId}`;

  return (
    <>
      <div className="no-print mb-6 flex flex-wrap items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={backTo}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Spausdinimo peržiūra</h1>
          <p className="text-muted-foreground">
            {situation.title}
            {/* Preview only — the sheet itself says nothing about being
                personalized, because to the child it is simply their sheet. */}
            {situation.personalized && (
              <span className="ml-2 text-primary">
                · pritaikyta
                {situation.personalizationTheme
                  ? ` (${situation.personalizationTheme})`
                  : ""}
              </span>
            )}
          </p>
        </div>

        <PrintOptions
          showNotes={showNotes}
          setShowNotes={setShowNotes}
          showAnswers={showAnswers}
          setShowAnswers={setShowAnswers}
          onePerPage={onePerPage}
          setOnePerPage={setOnePerPage}
          hasNotes={Boolean(situation.forGrownup)}
          hasAnswers={hasAnswers}
        />

        <Button onClick={() => window.print()}>
          <Printer className="mr-2 h-4 w-4" />
          Spausdinti
        </Button>
      </div>

      <div className="print-sheet mx-auto max-w-3xl">
        <PrintedSheet
          situation={situation}
          showAnswers={showAnswers}
          showNotes={showNotes}
          onePerPage={onePerPage}
        />
      </div>
    </>
  );
}

/**
 * The choices before printing, shared with the session print view.
 *
 * All three default to **off**, and that is the same rule each time: the common
 * case is the plain sheet for the child, and a checkbox nobody ticked has to
 * produce the safe, frugal document — no notes revealing what the situation
 * tests, no answers already filled in, and two pages rather than six.
 */
export function PrintOptions({
  showNotes,
  setShowNotes,
  showAnswers,
  setShowAnswers,
  onePerPage,
  setOnePerPage,
  hasNotes,
  hasAnswers,
}: {
  showNotes: boolean;
  setShowNotes: (next: boolean) => void;
  showAnswers: boolean;
  setShowAnswers: (next: boolean) => void;
  onePerPage: boolean;
  setOnePerPage: (next: boolean) => void;
  hasNotes: boolean;
  hasAnswers: boolean;
}) {
  return (
    <div className="mr-4 flex flex-wrap items-center gap-4">
      {hasNotes && (
        <div className="flex items-center gap-2">
          <Checkbox
            id="show-notes"
            checked={showNotes}
            onCheckedChange={(checked) => setShowNotes(checked === true)}
          />
          <Label htmlFor="show-notes" className="cursor-pointer text-sm">
            Suaugusiojo lapas
          </Label>
        </div>
      )}
      {hasAnswers && (
        <div className="flex items-center gap-2">
          <Checkbox
            id="show-answers"
            checked={showAnswers}
            onCheckedChange={(checked) => setShowAnswers(checked === true)}
          />
          <Label htmlFor="show-answers" className="cursor-pointer text-sm">
            Jau parašyti atsakymai
          </Label>
        </div>
      )}
      {/* A whole page per question — room to write by hand, which the
          counter-argument question in particular needs. */}
      <div className="flex items-center gap-2">
        <Checkbox
          id="one-per-page"
          checked={onePerPage}
          onCheckedChange={(checked) => setOnePerPage(checked === true)}
        />
        <Label htmlFor="one-per-page" className="cursor-pointer text-sm">
          Po vieną klausimą lape
        </Label>
      </div>
    </div>
  );
}
